import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';

/** The user's stored choice: an explicit theme, or follow the OS setting. */
export type ThemePreference = 'light' | 'dark' | 'system';

/** Shared across apps so the choice follows the user through the suite. */
const THEME_STORAGE_KEY = 'sisoma.theme';

/**
 * Owns the `data-theme` attribute on `<html>`, which activates the dark token
 * values in `styles/theme/_tokens.scss`. To avoid a flash of the wrong theme
 * before Angular boots, apps should also inline the small pre-boot script
 * documented in projects/ui/README.md — it applies the same attribute from
 * the same storage key before first paint; this service takes over from there.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  private readonly preferenceState = signal<ThemePreference>(readStoredPreference());
  private readonly systemDark = signal(false);

  /** The user's stored choice. */
  readonly preference = this.preferenceState.asReadonly();

  /** The theme actually applied to the page right now. */
  readonly resolvedTheme = computed<'light' | 'dark'>(() => {
    const preference = this.preferenceState();
    if (preference === 'system') {
      return this.systemDark() ? 'dark' : 'light';
    }
    return preference;
  });

  constructor() {
    const media = this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)');
    if (media) {
      this.systemDark.set(media.matches);
      media.addEventListener('change', (event) => {
        this.systemDark.set(event.matches);
        this.apply();
      });
    }
    this.apply();
  }

  setPreference(preference: ThemePreference): void {
    this.preferenceState.set(preference);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      // Storage unavailable (e.g. private browsing) — the choice lasts the session.
    }
    this.apply();
  }

  /** light → dark → system → light … */
  cyclePreference(): void {
    const order: ThemePreference[] = ['light', 'dark', 'system'];
    const next = order[(order.indexOf(this.preferenceState()) + 1) % order.length];
    this.setPreference(next);
  }

  private apply(): void {
    this.document.documentElement.setAttribute('data-theme', this.resolvedTheme());
  }
}

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    return 'system';
  }
}
